import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      showDetails: false,
      copied: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  handleRefresh = () => window.location.reload();

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  copyErrorDetails = async () => {
    const errorText = `Error: ${this.state.error?.toString()}\n\nStack Trace:\n${this.state.errorInfo?.componentStack}`;
    
    try {
      await navigator.clipboard.writeText(errorText);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch (err) {
      console.error('Failed to copy error details');
    }
  };

  render() {
    const { t } = this.props;
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-jc-card flex items-center justify-center p-4 relative overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 start-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }}></div>
            <div className="absolute bottom-1/4 end-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 start-1/2 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }}></div>
          </div>

          <div className="relative w-full max-w-2xl">
            {/* Main Error Card */}
            <div className="bg-jc-elevated  rounded-3xl shadow-2xl border border-jc-border overflow-hidden">
              {/* Header Section */}
              <div className="p-8 text-center">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full blur-xl opacity-50 animate-pulse"></div>
                    <div className="relative p-4 bg-gradient-to-br from-slate-700 to-slate-800 rounded-full border border-jc-border-strong">
                      <AlertTriangle className="w-12 h-12 text-purple-400" />
                    </div>
                  </div>
                </div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
                  {t('error.title')}
                </h1>
                <p className="text-jc-muted text-lg">{t('error.subtitle')}</p>
              </div>

              {/* Content Section */}
              <div className="px-8 pb-8">
                <div className="text-center mb-8">
                  <p className="text-jc-secondary text-base leading-relaxed">
                    {t('error.body')}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                  <button
                    onClick={this.handleRefresh}
                    className="group flex items-center justify-center space-x-2 rtl:space-x-reverse bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-8 py-3.5 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-purple-500/50"
                  >
                    <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
                    <span>{t('error.tryAgain')}</span>
                  </button>
                  
                  <button
                    onClick={() => window.location.href = '/'}
                    className="flex items-center justify-center space-x-2 rtl:space-x-reverse bg-jc-hover hover:bg-jc-hover text-jc-secondary px-8 py-3.5 rounded-xl font-semibold border border-jc-border-strong hover:border-slate-500 transition-all duration-300 transform hover:scale-105"
                  >
                    <Home className="w-5 h-5" />
                    <span>{t('error.goHome')}</span>
                  </button>
                </div>

                {/* Helpful Tips */}
                <div className="bg-slate-700/30 border border-jc-border-strong rounded-2xl p-6 mb-6 ">
                  <h3 className="font-semibold text-purple-300 mb-4 flex items-center text-sm uppercase tracking-wide">
                    <span className="me-2">💡</span> {t('error.whatYouCanTry')}
                  </h3>
                  <ul className="text-jc-secondary space-y-3 text-sm">
                    <li className="flex items-start space-x-3 rtl:space-x-reverse">
                      <span className="text-purple-400 mt-1 flex-shrink-0">•</span>
                      <span>{t('error.tip1')}</span>
                    </li>
                    <li className="flex items-start space-x-3 rtl:space-x-reverse">
                      <span className="text-purple-400 mt-1 flex-shrink-0">•</span>
                      <span>{t('error.tip2')}</span>
                    </li>
                    <li className="flex items-start space-x-3 rtl:space-x-reverse">
                      <span className="text-purple-400 mt-1 flex-shrink-0">•</span>
                      <span>{t('error.tip3')}</span>
                    </li>
                    <li className="flex items-start space-x-3 rtl:space-x-reverse">
                      <span className="text-purple-400 mt-1 flex-shrink-0">•</span>
                      <span>{t('error.tip4')}</span>
                    </li>
                  </ul>
                </div>

                
              </div>

              {/* Footer */}
              <div className="bg-jc-elevated px-8 py-4 border-t border-jc-border">
                <p className="text-center text-sm text-jc-muted">
                  {t('error.footer')}
                </p>
              </div>
            </div>

            {/* Decorative floating dots */}
            <div className="absolute -top-4 -end-4 w-3 h-3 bg-purple-400 rounded-full animate-pulse opacity-60"></div>
            <div className="absolute -bottom-4 -start-4 w-3 h-3 bg-pink-400 rounded-full animate-pulse opacity-60" style={{ animationDelay: '0.5s' }}></div>
            <div className="absolute top-1/2 -end-8 w-2 h-2 bg-blue-400 rounded-full animate-pulse opacity-40" style={{ animationDelay: '1s' }}></div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapped rather than hooked: this is a class component, so it cannot call
 * useTranslation. withTranslation injects `t` as a prop, which is the
 * supported way to translate one — and an error boundary MUST stay a class,
 * because componentDidCatch has no hook equivalent.
 */
export default withTranslation(['components', 'common'])(ErrorBoundary);